import React, { useEffect, useState, createContext, useContext, memo } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
export type MenuItem = {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  available: boolean;
  image?: string;
  sort_order?: number;
};
export type CartItem = {
  menuItem: MenuItem;
  quantity: number;
};
export type Order = {
  id: string;
  items: CartItem[];
  total: number;
  status: 'pendiente' | 'preparando' | 'listo' | 'entregado';
  customerName: string;
  customerPhone: string;
  date: string;
  type: 'delivery' | 'pickup';
};
export type Reservation = {
  id: string;
  name: string;
  phone: string;
  email: string;
  date: string;
  time: string;
  guests: number;
  notes?: string;
  status: 'pendiente' | 'confirmada' | 'cancelada';
};
export type Vacante = {
  id: string;
  title: string;
  description: string;
  requirements: string[];
  type: 'tiempo-completo' | 'medio-tiempo';
};
export type AdminUser = {
  id: string;
  username: string;
  password: string;
};
type StoreContextType = {
  menuItems: MenuItem[];
  categories: string[];
  menuLoading: boolean;
  cart: CartItem[];
  orders: Order[];
  reservations: Reservation[];
  vacantes: Vacante[];
  adminUsers: AdminUser[];
  isAdmin: boolean;
  addToCart: (item: MenuItem) => void;
  removeFromCart: (itemId: string) => void;
  updateCartQuantity: (itemId: string, qty: number) => void;
  clearCart: () => void;
  placeOrder: (customerName: string, customerPhone: string, type: 'delivery' | 'pickup') => void;
  updateOrderStatus: (orderId: string, status: Order['status']) => void;
  addReservation: (data: Omit<Reservation, 'id' | 'status'>) => void;
  updateReservationStatus: (id: string, status: Reservation['status']) => void;
  addMenuItem: (item: Omit<MenuItem, 'id'>) => void;
  updateMenuItem: (id: string, updates: Partial<MenuItem>) => void;
  deleteMenuItem: (id: string) => void;
  reorderItems: (category: string, orderedIds: string[]) => void;
  reorderCategories: (orderedNames: string[]) => void;
  addCategory: (name: string) => void;
  deleteCategory: (name: string) => void;
  adminLogin: (username: string, password: string) => boolean;
  adminLogout: () => void;
  createAdminUser: (username: string, password: string) => boolean;
  deleteAdminUser: (id: string) => void;
  cartTotal: number;
  cartCount: number;
};
const StoreContext = createContext<StoreContextType | undefined>(undefined);
const INITIAL_VACANTES: Vacante[] = [{
  id: '1',
  title: 'Cocinero',
  description: 'Buscamos cocinero con experiencia en parrilla.',
  requirements: ['2 años de experiencia', 'Disponibilidad horaria', 'Trabajo en equipo'],
  type: 'tiempo-completo'
}, {
  id: '2',
  title: 'Mesero/a',
  description: 'Atención al cliente y servicio de mesas.',
  requirements: ['Experiencia previa', 'Buena presencia', 'Inglés básico'],
  type: 'medio-tiempo'
}];
const INITIAL_ADMIN_USERS: AdminUser[] = [{
  id: '1',
  username: 'admin',
  password: 'cochera2025'
}];
export function StoreProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [vacantes, setVacantes] = useState<Vacante[]>(INITIAL_VACANTES);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>(INITIAL_ADMIN_USERS);
  const [isAdmin, setIsAdmin] = useState(false);
  // ── Load menu & categories ──
  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setMenuItems([]);
      setCategories([]);
      setMenuLoading(false);
      return;
    }
    async function loadData() {
      try {
        // Load Categories
        const {
          data: catData,
          error: catError
        } = await supabase!.from('categories').select('name').order('sort_order', {
          ascending: true
        });
        if (!catError && catData) {
          setCategories(catData.map((c: any) => c.name));
        }
        // Load Menu Items
        const {
          data,
          error
        } = await supabase!.from('menu_items').select('*').order('sort_order', {
          ascending: true
        }).order('name', {
          ascending: true
        });
        if (error) throw error;
        setMenuItems((data ?? []).map((row: any) => ({
          id: String(row.id),
          name: row.name,
          description: row.description ?? '',
          price: Number(row.price),
          category: row.category,
          available: row.available ?? true,
          image: row.image ?? undefined,
          sort_order: row.sort_order ?? 0
        })));
      } catch (err) {
        console.warn('Supabase fetch failed:', err);
      } finally {
        setMenuLoading(false);
      }
    }
    loadData();
    // Real-time: menu_items
    const menuChannel = supabase!.channel('menu_items_realtime').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'menu_items'
    }, payload => {
      if (payload.eventType === 'INSERT') {
        const row = payload.new as any;
        setMenuItems(prev => [...prev, {
          id: String(row.id),
          name: row.name,
          description: row.description ?? '',
          price: Number(row.price),
          category: row.category,
          available: row.available ?? true,
          image: row.image ?? undefined
        }]);
      } else if (payload.eventType === 'UPDATE') {
        const row = payload.new as any;
        setMenuItems(prev => prev.map(item => item.id === String(row.id) ? {
          ...item,
          id: String(row.id),
          name: row.name,
          description: row.description ?? '',
          price: Number(row.price),
          category: row.category,
          available: row.available ?? true,
          image: row.image ?? undefined,
          sort_order: row.sort_order ?? item.sort_order ?? 0
        } : item));
      } else if (payload.eventType === 'DELETE') {
        const row = payload.old as any;
        setMenuItems(prev => prev.filter(item => item.id !== String(row.id)));
      }
    }).subscribe();
    // Real-time: categories
    const catChannel = supabase!.channel('categories_realtime').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'categories'
    }, async () => {
      const {
        data
      } = await supabase!.from('categories').select('name').order('sort_order');
      if (data) setCategories(data.map((r: any) => r.name));
    }).subscribe();
    return () => {
      supabase!.removeChannel(menuChannel);
      supabase!.removeChannel(catChannel);
    };
  }, []);
  // ── Reorder items within a category ──
  const reorderItems = async (category: string, orderedIds: string[]) => {
    // Optimistic update
    setMenuItems(prev => {
      const otherItems = prev.filter(i => i.category !== category);
      const catItems = prev.filter(i => i.category === category);
      const reorderedCatItems = orderedIds.map(id => catItems.find(i => i.id === id)).filter(Boolean) as MenuItem[];
      // Update sort_order in memory
      reorderedCatItems.forEach((item, idx) => {
        item.sort_order = idx;
      });
      return [...otherItems, ...reorderedCatItems];
    });
    if (isSupabaseConfigured && supabase) {
      // Batch update is tricky in Supabase without RPC, so we loop (or use upsert)
      const updates = orderedIds.map((id, index) => ({
        id,
        sort_order: index
      }));
      for (const update of updates) {
        await supabase.from('menu_items').update({
          sort_order: update.sort_order
        }).eq('id', update.id);
      }
    }
  };
  // ── Reorder categories ──
  const reorderCategories = async (orderedNames: string[]) => {
    setCategories(orderedNames);
    if (isSupabaseConfigured && supabase) {
      const updates = orderedNames.map((name, index) => ({
        name,
        sort_order: index
      }));
      // We need IDs to update efficiently, but let's try updating by name
      for (const update of updates) {
        await supabase.from('categories').update({
          sort_order: update.sort_order
        }).eq('name', update.name);
      }
    }
  };
  // ── Add category ──
  const addCategory = async (name: string) => {
    if (categories.includes(name)) return;
    setCategories(prev => [...prev, name]);
    if (isSupabaseConfigured && supabase) {
      await supabase.from('categories').insert([{
        name,
        sort_order: categories.length
      }]);
    }
  };
  // ── Delete category ──
  const deleteCategory = async (name: string) => {
    setCategories(prev => prev.filter(c => c !== name));
    if (isSupabaseConfigured && supabase) {
      await supabase.from('categories').delete().eq('name', name);
    }
  };
  // ── Sync addMenuItem to Supabase ──
  const addMenuItem = async (item: Omit<MenuItem, 'id'>) => {
    if (isSupabaseConfigured && supabase) {
      const catItems = menuItems.filter(i => i.category === item.category);
      const {
        error
      } = await supabase.from('menu_items').insert([{
        ...item,
        sort_order: catItems.length
      }]);
      if (error) {
        console.error('❌ addMenuItem failed:', error.message, error.details);
      }
      // ✅ Don't setMenuItems here — realtime listener handles the INSERT event
      return;
    }
    // Fallback (no Supabase)
    const newItem: MenuItem = {
      ...item,
      id: Math.random().toString(36).substr(2, 9)
    };
    setMenuItems(prev => [...prev, newItem]);
  };
  // ── Sync updateMenuItem to Supabase ──
  const updateMenuItem = async (id: string, updates: Partial<MenuItem>) => {
    // Optimistic update FIRST so UI reflects change immediately
    setMenuItems(prev => prev.map(i => i.id === id ? {
      ...i,
      ...updates
    } : i));
    if (isSupabaseConfigured && supabase) {
      const {
        error
      } = await supabase.from('menu_items').update(updates).eq('id', id);
      if (error) {
        console.error('❌ updateMenuItem failed:', error.message, error.details);
      }
    }
  };
  // ── Sync deleteMenuItem to Supabase ──
  const deleteMenuItem = async (id: string) => {
    if (isSupabaseConfigured && supabase) {
      const {
        error
      } = await supabase.from('menu_items').delete().eq('id', id);
      if (error) {
        console.error('❌ deleteMenuItem failed:', error.message, error.details);
        return; // Don't remove locally if DB delete failed
      }
    }
    setMenuItems(prev => prev.filter(i => i.id !== id));
  };
  const addToCart = (item: MenuItem) => {
    setCart(prev => {
      const existing = prev.find(i => i.menuItem.id === item.id);
      if (existing) {
        return prev.map(i => i.menuItem.id === item.id ? {
          ...i,
          quantity: i.quantity + 1
        } : i);
      }
      return [...prev, {
        menuItem: item,
        quantity: 1
      }];
    });
  };
  const removeFromCart = (itemId: string) => {
    setCart(prev => prev.filter(i => i.menuItem.id !== itemId));
  };
  const updateCartQuantity = (itemId: string, qty: number) => {
    if (qty <= 0) {
      removeFromCart(itemId);
      return;
    }
    setCart(prev => prev.map(i => i.menuItem.id === itemId ? {
      ...i,
      quantity: qty
    } : i));
  };
  const clearCart = () => setCart([]);
  const placeOrder = (customerName: string, customerPhone: string, type: 'delivery' | 'pickup') => {
    const newOrder: Order = {
      id: Math.random().toString(36).substr(2, 9),
      items: [...cart],
      total: cart.reduce((sum, item) => sum + item.menuItem.price * item.quantity, 0),
      status: 'pendiente',
      customerName,
      customerPhone,
      date: new Date().toISOString(),
      type
    };
    setOrders(prev => [newOrder, ...prev]);
    clearCart();
  };
  const updateOrderStatus = (orderId: string, status: Order['status']) => {
    setOrders(prev => prev.map(o => o.id === orderId ? {
      ...o,
      status
    } : o));
  };
  const addReservation = (data: Omit<Reservation, 'id' | 'status'>) => {
    const newReservation: Reservation = {
      ...data,
      id: Math.random().toString(36).substr(2, 9),
      status: 'pendiente'
    };
    setReservations(prev => [newReservation, ...prev]);
  };
  const updateReservationStatus = (id: string, status: Reservation['status']) => {
    setReservations(prev => prev.map(r => r.id === id ? {
      ...r,
      status
    } : r));
  };
  const adminLogin = (username: string, password: string) => {
    const user = adminUsers.find(u => u.username === username && u.password === password);
    if (user) {
      setIsAdmin(true);
      return true;
    }
    return false;
  };
  const adminLogout = () => {
    setIsAdmin(false);
  };
  const createAdminUser = (username: string, password: string): boolean => {
    if (adminUsers.find(u => u.username === username)) return false;
    const newUser: AdminUser = {
      id: Math.random().toString(36).substr(2, 9),
      username,
      password
    };
    setAdminUsers(prev => [...prev, newUser]);
    return true;
  };
  const deleteAdminUser = (id: string) => {
    setAdminUsers(prev => prev.filter(u => u.id !== id));
  };
  const cartTotal = cart.reduce((sum, item) => sum + item.menuItem.price * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  return <StoreContext.Provider value={{
    menuItems,
    categories,
    menuLoading,
    cart,
    orders,
    reservations,
    vacantes,
    adminUsers,
    isAdmin,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    placeOrder,
    updateOrderStatus,
    addReservation,
    updateReservationStatus,
    addMenuItem,
    updateMenuItem,
    deleteMenuItem,
    reorderItems,
    reorderCategories,
    addCategory,
    deleteCategory,
    adminLogin,
    adminLogout,
    createAdminUser,
    deleteAdminUser,
    cartTotal,
    cartCount
  }} data-id="element-210">
      {children}
    </StoreContext.Provider>;
}
export function useStore() {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
}